import React, { useState, useRef, useEffect } from 'react';
import { TouchableOpacity, Text, View, StyleSheet, ActivityIndicator, Platform, Animated, Dimensions } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores } from '../../../../styles/variablesCss';
import { useCart } from '../../../../context/CartContext';
import { useUser } from '../../../../context/UserContext';
import Login from '../AdminUsuario/Login';
import { trackAddToCart } from '../../../../utils/analytics';

const AddCarrito = ({ plu, cantidad, style, onSuccess, showText = true, sucursalOverride, onCartStatusUpdate, disabled = false, productName = '', productPrice = 0, productCategory = '' }) => {
  const [loading, setLoading] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [estaEnCarrito, setEstaEnCarrito] = useState(false);
  const [cantidadEnCarrito, setCantidadEnCarrito] = useState(0);
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const opacityAnim = useRef(new Animated.Value(1)).current;
  const [showLogin, setShowLogin] = useState(false);
  
  // Detectar si estamos en móvil web
  const windowWidth = Platform.OS === 'web' ? (typeof window !== 'undefined' ? window.innerWidth : 768) : Dimensions.get('window').width;
  const isSmallWeb = Platform.OS === 'web' && windowWidth < 768;
  
  // Obtener funciones y estado del contexto del carrito
  const { verificarProductoEnCarrito: checkCartProduct, agregarAlCarrito: addToCart } = useCart();
  const { codUsu, mail } = useUser();
  
  // Función para verificar si el producto está en el carrito
  const verificarProductoEnCarrito = async () => {
    try {
      const result = await checkCartProduct(plu);
      
      // Actualizar estados locales
      setEstaEnCarrito(result.estaEnCarrito);
      setCantidadEnCarrito(result.cantidad);
      
      // Notificar al componente padre si es necesario
      if (onCartStatusUpdate) {
        onCartStatusUpdate(result.estaEnCarrito, result.cantidad);
      }
      
      return result;
    } catch (err) {
      console.error('Error al verificar carrito:', err);
      setEstaEnCarrito(false);
      setCantidadEnCarrito(0);
      
      if (onCartStatusUpdate) {
        onCartStatusUpdate(false, 0);
      }
      
      return { estaEnCarrito: false, cantidad: 0 };
    }
  };
  
  // Efecto para resetear el estado después de mostrar "AGREGADO"
  useEffect(() => {
    if (isAdded) {
      const timer = setTimeout(() => {
        setIsAdded(false);
      }, 500); // Mostrar por 0.5 segundo
      return () => clearTimeout(timer);
    }
  }, [isAdded]);
  
 
  
  // Escuchar eventos de actualización del carrito
  useEffect(() => {
    // Solo añadir el listener si estamos en web
    if (Platform.OS === 'web' && typeof document !== 'undefined' && plu) {
      // Limpiar el event listener al desmontar
      return () => {};
    }
  }, [plu]);
  
  // Función para animar el botón
  const animateButton = () => {
    // Secuencia de animación: pulso + fade
    Animated.sequence([
      // Pulsar hacia afuera
      Animated.timing(scaleAnim, {
        toValue: 1.1,
        duration: 50,
        useNativeDriver: Platform.OS !== 'web'
      }),
      // Volver a tamaño normal
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 50,
        useNativeDriver: Platform.OS !== 'web'
      })
    ]).start();
  };

  const agregarAlCarrito = async () => {
    try {
      if (disabled) return;
      // Verificar login requerido: codUsu y mail
      if (!codUsu || !mail) {
        setShowLogin(true);
        return;
      }
      setLoading(true);
      
      // Usar la función centralizada del contexto para agregar al carrito
      const result = await addToCart(plu, cantidad, sucursalOverride);
      
      if (result.success) {
        // Actualizar estados locales
        setEstaEnCarrito(true);
        const newCantidad = cantidadEnCarrito + parseInt(cantidad);
        setCantidadEnCarrito(newCantidad);
        
        // Animaciones y notificaciones visuales
        animateButton();
        setIsAdded(true);
        
        // Notificar al componente padre del estado actualizado del carrito
        if (onCartStatusUpdate) {
          onCartStatusUpdate(true, newCantidad);
        }
        
        // Si se proporcionó una función de éxito, llamarla con los datos del carrito
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(result.data);
        }

        // Enviar evento de add_to_cart a Google Analytics
        trackAddToCart({
          plu,
          cantidad,
          nombre: productName || '',
          precio: productPrice || 0
        });

      } else {
        
      }
    } catch (error) {
      console.error('Error en AddCarrito:', error);
    } finally {
      // Volver a verificar el estado del producto en el carrito
      verificarProductoEnCarrito();
      setLoading(false);
    }
  };

  return (
    <Animated.View style={{
      transform: [{scale: scaleAnim}],
      opacity: opacityAnim,
      ...(!isSmallWeb ? { width: '100%' } : {})
    }}>
      <TouchableOpacity
        style={[styles.botonCarrito, style, isAdded ? styles.botonAgregado : null, disabled ? styles.botonDisabled : null]}
        onPress={agregarAlCarrito}
        disabled={loading || isAdded || disabled}
        className="add_cart"
      >
        {loading ? (
          <ActivityIndicator size="small" color={colores.blanco} />
        ) : (
          <>
            {isSmallWeb && !isAdded ? (
              <View style={styles.iconContainer}>
                <MaterialCommunityIcons 
                  name="cart" 
                  size={20} 
                  color="white" 
                />
                <MaterialCommunityIcons 
                  name="plus" 
                  size={18} 
                  color="white" 
                  style={styles.plusIcon}
                />
              </View>
            ) : (
              <>
                <MaterialCommunityIcons 
                  name={isAdded ? "check-circle" : "cart"} 
                  size={22} 
                  color="white" 
                />
                {showText && !isSmallWeb && (
                  <Text style={styles.carritoText}>
                    {isAdded ? "AGREGADO" : "AGREGAR"}
                  </Text>
                )}
              </>
            )}
          </>
        )}  
      </TouchableOpacity>
      {/* Modal de Login cuando el usuario no está autenticado */}
      <Login visible={showLogin} onClose={() => setShowLogin(false)} />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  botonCarrito: {
    backgroundColor: colores.rojo,
    borderRadius: 5,
    paddingVertical: 8,
    paddingHorizontal: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: Platform.OS === 'web' ? 'all 0.3s ease' : undefined,
  },
  botonAgregado: {
    backgroundColor: '#28a745', // Color verde para confirmación
  },
  botonDisabled: {
    backgroundColor: '#BDBDBD',
  },
  carritoText: {
    color: colores.blanco,
    fontWeight: 'bold',
    marginLeft: 5,
  },
  iconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  plusIcon: {
    marginLeft: 2,
  },
});

export default AddCarrito;
